import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { sbGetSetting, sbSetSetting, sbFetch } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import { useToast } from "./Toast";
import PullToRefresh from "./PullToRefresh";
import PublicationCalendar from "./PublicationCalendar";
import PreviewModal from "./PreviewModal";
import { SVG_ICONS } from "./TablerIconPicker";
import { renderLessonBodyHtml, parseMarkdownToBody } from "../utils/lessonMarkdown";
import { parseMarkdown } from "../utils/markdownParser";
import { getIcon } from "../utils/iconValidator";
import "../styles/ListTab.css";

const ReportsTab = forwardRef((props, ref) => {
  const { error, success } = useToast();
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState("calendar");
  const [activeSubtab, setActiveSubtab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [menuVisible, setMenuVisible] = useState(() => {
    // Always show menu by default, but respect user's collapse preference after first load
    const saved = localStorage.getItem("reportsMenuVisible");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showNextMonth, setShowNextMonth] = useState(() => {
    const saved = localStorage.getItem("showNextMonth");
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [unscheduledExpanded, setUnscheduledExpanded] = useState(() => {
    const saved = localStorage.getItem("unscheduledExpanded");
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [filtersExpanded, setFiltersExpanded] = useState(() => {
    const saved = localStorage.getItem("filtersExpanded");
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [selectedContentTypes, setSelectedContentTypes] = useState(() => {
    const saved = localStorage.getItem("selectedContentTypes");
    return saved ? JSON.parse(saved) : ["Lesson", "Prompt", "Resource", "News"];
  });

  useEffect(() => {
    localStorage.setItem("reportsMenuVisible", JSON.stringify(menuVisible));
  }, [menuVisible]);

  useEffect(() => {
    localStorage.setItem("showNextMonth", JSON.stringify(showNextMonth));
  }, [showNextMonth]);

  useEffect(() => {
    localStorage.setItem("unscheduledExpanded", JSON.stringify(unscheduledExpanded));
  }, [unscheduledExpanded]);

  useEffect(() => {
    localStorage.setItem("selectedContentTypes", JSON.stringify(selectedContentTypes));
  }, [selectedContentTypes]);

  useEffect(() => {
    localStorage.setItem("filtersExpanded", JSON.stringify(filtersExpanded));
  }, [filtersExpanded]);

  useEffect(() => {
    loadAllItems();
  }, []);

  useImperativeHandle(ref, () => ({
    loadAllItems
  }));

  const loadAllItems = async () => {
    try {
      setLoading(true);

      // Load all content types
      const [lessons, prompts, resources, newsData] = await Promise.all([
        sbGetSetting("origamic-lessons").catch(() => []),
        sbGetSetting("origamic-prompts").catch(() => []),
        sbGetSetting("origamic-resources").catch(() => []),
        sbFetch("GET", "news?order=published_at.desc").catch(() => [])
      ]);

      // Combine all items with type information
      const items = [
        ...((Array.isArray(lessons) ? lessons : []).map(l => ({ ...l, contentType: "Lesson", storageKey: "origamic-lessons", draft: l.draft ?? false }))),
        ...((Array.isArray(prompts) ? prompts : []).map(p => ({ ...p, contentType: "Prompt", storageKey: "origamic-prompts", draft: p.draft ?? false }))),
        ...((Array.isArray(resources) ? resources : []).map(r => ({ ...r, contentType: "Resource", storageKey: "origamic-resources", draft: r.draft ?? false }))),
        ...((Array.isArray(newsData) ? newsData : []).map(n => ({ ...n, contentType: "News", storageKey: "news", draft: n.archived ?? false })))
      ];

      // Backfill publishedDate for published items that don't have it
      const itemsWithPublishedDate = items.map(item => {
        if (!item.draft && !item.publishedDate && item.contentType !== "News") {
          return { ...item, publishedDate: item.addedAt || new Date().toISOString() };
        }
        return item;
      });

      // Save backfilled published dates to storage for each content type
      const groupsByStorage = {};
      itemsWithPublishedDate.forEach(item => {
        if (item.storageKey && item.storageKey !== "news") {
          if (!groupsByStorage[item.storageKey]) groupsByStorage[item.storageKey] = [];
          const { contentType, storageKey, ...rest } = item;
          groupsByStorage[item.storageKey].push(rest);
        }
      });

      // Persist to storage (lessons, prompts, resources)
      for (const [storageKey, itemsToSave] of Object.entries(groupsByStorage)) {
        await sbSetSetting(storageKey, itemsToSave);
      }

      // Sort by date
      itemsWithPublishedDate.sort((a, b) => new Date(b.addedAt || b.published_at) - new Date(a.addedAt || a.published_at));

      setAllItems(itemsWithPublishedDate);
    } catch (err) {
      console.error("Error loading items:", err);
      error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDraft = async (item) => {
    try {
      const storageKey = item.storageKey;
      const newDraftStatus = !item.draft;

      // When publishing, set publishedDate if not already set
      const publishedDate = !newDraftStatus && !item.publishedDate ? new Date().toISOString() : item.publishedDate;

      const updated = allItems.map(i =>
        i.id === item.id && i.storageKey === storageKey
          ? { ...i, draft: newDraftStatus, publishedDate }
          : i
      );

      if (storageKey === "news") {
        // For news items, update archived status (inverse of draft)
        const newArchivedStatus = newDraftStatus;
        await sbFetch("PATCH", `news?id=eq.${item.id}`, {
          archived: newArchivedStatus,
          published_at: publishedDate
        });
      } else {
        // Update storage for lessons, prompts, resources
        const itemsOfType = updated
          .filter(i => i.storageKey === storageKey)
          .map(({ contentType, storageKey, ...rest }) => rest);
        await sbSetSetting(storageKey, itemsOfType);
      }

      // Send notification only when publishing (transitioning from draft to published)
      if (item.draft && !newDraftStatus) {
        await sendNotification(
          `New ${item.contentType}: ${item.title}`,
          item.subtitle || `A new ${item.contentType.toLowerCase()} is available.`,
          item.contentType.toLowerCase(),
          item.id
        );
      }

      setAllItems(updated);
    } catch (err) {
      console.error("Error toggling draft status:", err);
      alert("Failed to update item");
    }
  };

  const getScheduleStatus = (scheduledDate) => {
    if (!scheduledDate) return null;

    const now = new Date();
    const scheduled = new Date(scheduledDate);
    const hoursUntil = (scheduled - now) / (1000 * 60 * 60);

    if (hoursUntil < 0) {
      return { label: "Late", emoji: "⚠️", color: "#EF4444", bgColor: "rgba(239, 68, 68, 0.2)" };
    } else if (hoursUntil <= 48) {
      return { label: "Coming Soon", emoji: "", color: "#F59E0B", bgColor: "rgba(245, 158, 11, 0.2)" };
    }
    return null;
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined
    });
  };

  const handleEditItem = (item) => {
    setEditingId(item.id);
    setEditFormData({ ...item });
  };

  const handleRescheduleItem = async (item, newDateStr) => {
    try {
      const [year, month, day] = newDateStr.split('-');
      const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0);
      const newDate = localDate.toISOString();

      // Create updated item with new scheduled date
      const updatedItem = { ...item, scheduled: newDate };

      // Update allItems
      const updated = allItems.map(i => i.id === item.id ? updatedItem : i);
      setAllItems(updated);

      // Save to appropriate storage
      if (item.contentType === "Lesson") {
        const lessons = updated.filter(i => i.contentType === "Lesson");
        await sbSetSetting("origamic-lessons", lessons);
      } else if (item.contentType === "Prompt") {
        const prompts = updated.filter(i => i.contentType === "Prompt");
        await sbSetSetting("origamic-prompts", prompts);
      } else if (item.contentType === "Resource") {
        const resources = updated.filter(i => i.contentType === "Resource");
        await sbSetSetting("origamic-resources", resources);
      } else if (item.contentType === "News") {
        await sbFetch("PATCH", `news?id=eq.${item.id}`, updatedItem, { Prefer: "return=minimal" });
      }

      success(`${item.contentType} rescheduled to ${newDateStr}`);
    } catch (err) {
      console.error("Error rescheduling item:", err);
      error("Failed to reschedule item");
    }
  };

  const handleUnscheduleItem = async (item) => {
    try {
      // Create updated item with cleared scheduled date
      const updatedItem = { ...item, scheduled: null, scheduled_date: null };

      // Update allItems
      const updated = allItems.map(i => i.id === item.id ? updatedItem : i);
      setAllItems(updated);

      // Save to appropriate storage
      if (item.contentType === "Lesson") {
        const lessons = updated.filter(i => i.contentType === "Lesson");
        await sbSetSetting("origamic-lessons", lessons);
      } else if (item.contentType === "Prompt") {
        const prompts = updated.filter(i => i.contentType === "Prompt");
        await sbSetSetting("origamic-prompts", prompts);
      } else if (item.contentType === "Resource") {
        const resources = updated.filter(i => i.contentType === "Resource");
        await sbSetSetting("origamic-resources", resources);
      } else if (item.contentType === "News") {
        await sbFetch("PATCH", `news?id=eq.${item.id}`, updatedItem, { Prefer: "return=minimal" });
      }

      success(`${item.contentType} marked as unscheduled`);
    } catch (err) {
      console.error("Error unscheduling item:", err);
      error("Failed to unschedule item");
    }
  };

  const handleSaveEdit = async () => {
    try {
      const item = allItems.find(i => i.id === editingId);
      if (!item) return;

      const wasPublished = item.draft && !editFormData.draft;

      // Update the item in allItems
      const updated = allItems.map(i => i.id === editingId ? editFormData : i);
      setAllItems(updated);

      // Save to appropriate storage based on content type
      if (editFormData.contentType === "Lesson") {
        const lessons = updated.filter(i => i.contentType === "Lesson");
        await sbSetSetting("origamic-lessons", lessons);
      } else if (editFormData.contentType === "Prompt") {
        const prompts = updated.filter(i => i.contentType === "Prompt");
        await sbSetSetting("origamic-prompts", prompts);
      } else if (editFormData.contentType === "Resource") {
        const resources = updated.filter(i => i.contentType === "Resource");
        await sbSetSetting("origamic-resources", resources);
      } else if (editFormData.contentType === "News") {
        await sbFetch("PATCH", `news?id=eq.${editFormData.id}`, editFormData, { Prefer: "return=minimal" });
      }

      if (wasPublished) {
        await sendNotification(
          `New ${editFormData.contentType}: ${editFormData.title || editFormData.name}`,
          editFormData.subtitle || "New content is available.",
          "learn",
          editFormData.id
        );
      }

      setEditingId(null);
      setEditFormData({});
      success(`${editFormData.contentType} updated`);
    } catch (err) {
      console.error("Error saving edit:", err);
      error("Failed to save changes");
    }
  };

  const handleViewItem = (item) => {
    setPreviewTitle(item.title || item.name || "Preview");

    let contentToDisplay = "";
    if (item.contentType === "Lesson") {
      let bodyHtml = '<div style="color:#7A9CC4">No content yet.</div>';
      if (item.body) {
        // body can be either a markdown string or an array
        const bodyArray = typeof item.body === 'string' ? parseMarkdownToBody(item.body) : item.body;
        if (Array.isArray(bodyArray) && bodyArray.length > 0) {
          bodyHtml = renderLessonBodyHtml(bodyArray);
        }
      }
      const lessonHtml = `
        <div style="max-width: 680px; margin: 0 auto; padding: 32px 24px;">
          <div style="background: rgba(59, 130, 246, 0.08); border: 1px solid rgba(59, 130, 246, 0.2); border-radius: 12px; padding: 32px 24px; margin-bottom: 32px; text-align: center;">
            <div style="font-size: 12px; color: #7A9CC4; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 16px; font-weight: 600;">${item.tag || "Lesson"}</div>
            <div style="font-size: 28px; font-weight: 700; margin-bottom: 16px; color: #fff;">${item.title || "Untitled Lesson"}</div>
            ${item.subtitle ? `<div style="font-size: 14px; color: #9DB4D4; line-height: 1.6;">${item.subtitle}</div>` : ""}
          </div>
          <div style="color: rgba(255,255,255,0.85); line-height: 1.6;">
            ${bodyHtml}
          </div>
        </div>
      `;
      contentToDisplay = lessonHtml;
    } else if (item.contentType === "Prompt") {
      contentToDisplay = parseMarkdown(item.text || "");
    } else if (item.contentType === "Resource") {
      const resourceHtml = `
        <div style="padding: 24px;">
          <div style="margin-bottom: 20px;">
            <h3 style="color: #5B9EF0; margin: 0 0 12px 0; font-size: 18px;">${item.title || "Resource"}</h3>
            ${item.description ? `<p style="color: #7A9CC4; margin: 0; line-height: 1.6;">${item.description}</p>` : ""}
          </div>
          <a href="${item.url}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 10px 16px; background: rgba(91, 158, 240, 0.2); border: 1px solid rgba(91, 158, 240, 0.4); color: #5B9EF0; text-decoration: none; border-radius: 6px; word-break: break-all;">${item.url}</a>
        </div>
      `;
      contentToDisplay = resourceHtml;
    } else if (item.contentType === "News") {
      const newsContent = parseMarkdown(item.what_does_it_mean_to_me || "");
      const newsHtml = `
        <div style="padding: 24px;">
          <div style="margin-bottom: 24px;">
            ${newsContent}
          </div>
          ${item.url ? `<a href="${item.url}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 10px 16px; background: rgba(91, 158, 240, 0.2); border: 1px solid rgba(91, 158, 240, 0.4); color: #5B9EF0; text-decoration: none; border-radius: 6px; word-break: break-all; margin-top: 12px;">Read full article</a>` : ""}
        </div>
      `;
      contentToDisplay = newsHtml;
    }

    setPreviewContent(contentToDisplay);
    setPreviewOpen(true);
  };

  if (loading) return <div className="loading">Loading report data...</div>;

  // Filter items by subtab
  const pendingItems = allItems.filter(item => item.draft && item.scheduled);
  const publishedItems = allItems.filter(item => item.draft === false);
  const unscheduledItems = allItems.filter(item => item.draft && !item.scheduled);

  const displayItems = activeSubtab === "all" ? allItems : (activeSubtab === "pending" ? pendingItems : activeSubtab === "published" ? publishedItems : unscheduledItems);

  // Group items by type
  const groupedItems = {};
  displayItems.forEach(item => {
    if (!groupedItems[item.contentType]) {
      groupedItems[item.contentType] = [];
    }
    groupedItems[item.contentType].push(item);
  });

  // Filter by search query and category
  const filteredGroupedItems = {};
  Object.keys(groupedItems).forEach(type => {
    // Skip if category filter is active and doesn't match
    if (selectedCategory && type !== selectedCategory) return;

    const filtered = groupedItems[type].filter(item =>
      item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.subtitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tag?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      filteredGroupedItems[type] = filtered;
    }
  });

  const typeOrder = ["Lesson", "Prompt", "Resource", "News"];
  const sortedTypes = Object.keys(filteredGroupedItems).sort((a, b) =>
    typeOrder.indexOf(a) - typeOrder.indexOf(b)
  );

  return (
    <div className="list-container" style={{ display: "flex", gap: menuVisible ? "40px" : "0px", height: "calc(100vh - 120px)", transition: "gap 0.3s ease" }}>
      {/* Left Sidebar Menu */}
      <aside style={{
        width: menuVisible ? "180px" : "0px",
        minWidth: menuVisible ? "180px" : "0px",
        flexShrink: 0,
        borderRight: menuVisible ? "1px solid rgba(59, 130, 246, 0.15)" : "none",
        paddingRight: menuVisible ? "20px" : "0px",
        overflowY: "auto",
        overflowX: "hidden",
        transition: "all 0.3s ease",
        opacity: menuVisible ? 1 : 0,
        pointerEvents: menuVisible ? "auto" : "none"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h3 style={{ color: "#5B9EF0", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", margin: "0", letterSpacing: "0.5px" }}>
            Reports
          </h3>
          <button
            onClick={() => setMenuVisible(!menuVisible)}
            style={{
              background: "transparent",
              border: "none",
              color: "#7A9CC4",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "color 0.2s"
            }}
            onMouseEnter={(e) => e.target.style.color = "#5B9EF0"}
            onMouseLeave={(e) => e.target.style.color = "#7A9CC4"}
            title={menuVisible ? "Hide menu" : "Show menu"}
          >
            {SVG_ICONS[menuVisible ? "chevron-left" : "chevron-right"]({ size: 16 })}
          </button>
        </div>
        <button
          onClick={() => setActiveReport("calendar")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "10px 12px",
            background: activeReport === "calendar" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeReport === "calendar" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeReport === "calendar" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            textAlign: "left",
            transition: "all 0.2s",
            marginBottom: "8px",
          }}
        >
          {SVG_ICONS["calendar"]({ size: 16, color: activeReport === "calendar" ? "#5B9EF0" : "#7A9CC4" })}
          Publication Calendar
        </button>
        <button
          onClick={() => setActiveReport("report")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "10px 12px",
            background: activeReport === "report" ? "rgba(59, 130, 246, 0.2)" : "transparent",
            border: activeReport === "report" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent",
            color: activeReport === "report" ? "#5B9EF0" : "#7A9CC4",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            textAlign: "left",
            transition: "all 0.2s",
          }}
        >
          {SVG_ICONS["chart-bar"]({ size: 16, color: activeReport === "report" ? "#5B9EF0" : "#7A9CC4" })}
          Publication Report
        </button>

        {/* Filters Section - Only show when menu is visible and calendar/report selected */}
        {menuVisible && (activeReport === "calendar" || activeReport === "report") && (
          <div style={{ marginTop: "24px", paddingTop: "16px", borderTop: "1px solid rgba(59, 130, 246, 0.15)" }}>
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              style={{
                width: "100%",
                padding: "0",
                background: "transparent",
                border: "none",
                color: "#5B9EF0",
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: "600",
                textTransform: "uppercase",
                margin: "0 0 12px 0",
                letterSpacing: "0.5px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                justifyContent: "space-between"
              }}
            >
              <span>Filters</span>
              {SVG_ICONS[filtersExpanded ? "chevron-left" : "chevron-right"]({ size: 14, color: "#5B9EF0" })}
            </button>

            {filtersExpanded && (
              <>
                <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ width: "100%", padding: "8px 10px", marginBottom: "12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff", fontSize: "12px", boxSizing: "border-box" }} />
            {activeReport === "calendar" && (
              <button onClick={() => setShowNextMonth(!showNextMonth)} style={{ width: "100%", padding: "8px 12px", marginBottom: "12px", background: showNextMonth ? "rgba(59, 130, 246, 0.2)" : "transparent", border: showNextMonth ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent", color: showNextMonth ? "#5B9EF0" : "#7A9CC4", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "500", textAlign: "left", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "6px" }}>
                {getIcon("eye")({ size: 14, color: showNextMonth ? "#5B9EF0" : "#7A9CC4" })}
                2 Month View
              </button>
            )}
            <h4 style={{ color: "#5B9EF0", fontSize: "11px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Status</h4>
            <button onClick={() => setActiveSubtab("all")} style={{ width: "100%", padding: "8px 12px", marginBottom: "6px", background: activeSubtab === "all" ? "rgba(59, 130, 246, 0.2)" : "transparent", border: activeSubtab === "all" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent", color: activeSubtab === "all" ? "#5B9EF0" : "#7A9CC4", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "500", textAlign: "left", transition: "all 0.2s" }}>All</button>
            <button onClick={() => setActiveSubtab("pending")} style={{ width: "100%", padding: "8px 12px", marginBottom: "6px", background: activeSubtab === "pending" ? "rgba(59, 130, 246, 0.2)" : "transparent", border: activeSubtab === "pending" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent", color: activeSubtab === "pending" ? "#5B9EF0" : "#7A9CC4", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "500", textAlign: "left", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "6px" }}>
              {SVG_ICONS["hourglass"]({ size: 14, color: activeSubtab === "pending" ? "#5B9EF0" : "#7A9CC4" })}
              Pending
            </button>
            <button onClick={() => setActiveSubtab("published")} style={{ width: "100%", padding: "8px 12px", marginBottom: "6px", background: activeSubtab === "published" ? "rgba(59, 130, 246, 0.2)" : "transparent", border: activeSubtab === "published" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid transparent", color: activeSubtab === "published" ? "#5B9EF0" : "#7A9CC4", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "500", textAlign: "left", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "6px" }}>
              {SVG_ICONS["check-circle"]({ size: 14, color: activeSubtab === "published" ? "#5B9EF0" : "#7A9CC4" })}
              Published
            </button>
            <button onClick={() => setActiveSubtab("unscheduled")} style={{ width: "100%", padding: "8px 12px", marginBottom: "12px", background: activeSubtab === "unscheduled" ? "rgba(239, 68, 68, 0.2)" : "transparent", border: activeSubtab === "unscheduled" ? "1px solid rgba(239, 68, 68, 0.4)" : "1px solid transparent", color: activeSubtab === "unscheduled" ? "#EF4444" : "#7A9CC4", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "500", textAlign: "left", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "6px" }}>
              {SVG_ICONS["alert-circle"]({ size: 14, color: activeSubtab === "unscheduled" ? "#EF4444" : "#7A9CC4" })}
              Unscheduled
            </button>
            <h4 style={{ color: "#5B9EF0", fontSize: "11px", fontWeight: "600", textTransform: "uppercase", margin: "12px 0 12px 0", letterSpacing: "0.5px" }}>Category</h4>
            <button onClick={() => setSelectedCategory(null)} style={{ width: "100%", padding: "6px 10px", marginBottom: "6px", background: selectedCategory === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)", border: selectedCategory === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.15)", color: selectedCategory === null ? "#5B9EF0" : "#7A9CC4", borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontWeight: "600", textAlign: "left", transition: "all 0.2s" }}>All Types</button>
            {typeOrder.map((category) => (
              <button key={category} onClick={() => setSelectedCategory(category)} style={{ width: "100%", padding: "6px 10px", marginBottom: "6px", background: selectedCategory === category ? `rgba(${category === "Lesson" ? "52, 211, 153" : category === "Prompt" ? "168, 85, 247" : category === "Resource" ? "245, 158, 11" : "239, 68, 68"}, 0.2)` : "rgba(59, 130, 246, 0.05)", border: selectedCategory === category ? `1px solid rgba(${category === "Lesson" ? "52, 211, 153" : category === "Prompt" ? "168, 85, 247" : category === "Resource" ? "245, 158, 11" : "239, 68, 68"}, 0.4)` : "1px solid rgba(59, 130, 246, 0.15)", color: selectedCategory === category ? (category === "Lesson" ? "#34D399" : category === "Prompt" ? "#A855F7" : category === "Resource" ? "#F59E0B" : "#EF4444") : "#7A9CC4", borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontWeight: "600", textAlign: "left", transition: "all 0.2s" }}>{category}</button>
            ))}
              </>
            )}

            {/* Unscheduled Items Section */}
            {activeReport === "calendar" && (
              <div style={{ marginTop: "24px", paddingTop: "16px", borderTop: "1px solid rgba(59, 130, 246, 0.15)" }}>
                <button
                  onClick={() => setUnscheduledExpanded(!unscheduledExpanded)}
                  style={{
                    width: "100%",
                    padding: "0",
                    background: "transparent",
                    border: "none",
                    color: "#EF4444",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "600",
                    textTransform: "uppercase",
                    margin: "0 0 12px 0",
                    letterSpacing: "0.5px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    justifyContent: "space-between"
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {SVG_ICONS["alert-circle"]({ size: 14, color: "#EF4444" })}
                    Unscheduled
                  </span>
                  {SVG_ICONS[unscheduledExpanded ? "chevron-left" : "chevron-right"]({ size: 14, color: "#EF4444" })}
                </button>

                {unscheduledExpanded && (
                  <>
                    {/* Content Type Filters */}
                    <div style={{ display: "flex", gap: "4px", marginBottom: "12px", flexWrap: "wrap" }}>
                      {["Lesson", "Prompt", "Resource", "News"].map(type => {
                        const unscheduledCount = (allItems?.filter(item =>
                          item.draft && !item.scheduled && !item.scheduled_date && item.contentType === type
                        ) || []).length;
                        return (
                          <button
                            key={`sidebar-filter-${type}`}
                            onClick={() => {
                              if (selectedContentTypes.includes(type)) {
                                setSelectedContentTypes(selectedContentTypes.filter(t => t !== type));
                              } else {
                                setSelectedContentTypes([...selectedContentTypes, type]);
                              }
                            }}
                            style={{
                              padding: "4px 8px",
                              background: selectedContentTypes.includes(type) ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.05)",
                              border: selectedContentTypes.includes(type) ? "1px solid rgba(239, 68, 68, 0.4)" : "1px solid rgba(239, 68, 68, 0.15)",
                              color: selectedContentTypes.includes(type) ? "#EF4444" : "#7A9CC4",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "10px",
                              fontWeight: "600",
                              transition: "all 0.2s",
                              whiteSpace: "nowrap"
                            }}
                          >
                            {type} ({unscheduledCount})
                          </button>
                        );
                      })}
                    </div>

                    {/* Unscheduled Items List */}
                    {(() => {
                      const unscheduledItems = allItems?.filter(item =>
                        item.draft && !item.scheduled && !item.scheduled_date
                      ) || [];

                      const groupedByType = {};
                      unscheduledItems.forEach(item => {
                        if (!groupedByType[item.contentType]) {
                          groupedByType[item.contentType] = [];
                        }
                        groupedByType[item.contentType].push(item);
                      });

                      const typeOrder = ["Lesson", "Prompt", "Resource", "News"];
                      const allTypes = Object.keys(groupedByType).sort((a, b) =>
                        typeOrder.indexOf(a) - typeOrder.indexOf(b)
                      );

                      const filteredTypes = allTypes.filter(type => selectedContentTypes.includes(type));

                      return (
                        <div
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            try {
                              const data = e.dataTransfer.getData("application/json");
                              if (data) {
                                const item = JSON.parse(data);
                                handleUnscheduleItem(item);
                              }
                            } catch (err) {
                              console.error("Error dropping item to unscheduled:", err);
                            }
                          }}
                          style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "400px", overflowY: "auto", transition: "background-color 0.2s" }}
                        >
                          {filteredTypes.length === 0 && unscheduledItems.length === 0 && (
                            <div style={{ color: "#7A9CC4", fontSize: "11px", padding: "8px", textAlign: "center" }}>
                              No unscheduled items
                            </div>
                          )}
                          {filteredTypes.map(type => (
                            <div key={type}>
                              <h5 style={{ margin: "0 0 6px 0", color: "#EF4444", fontSize: "10px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                {type}
                              </h5>
                              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                {groupedByType[type].map((item, idx) => (
                                  <div
                                    key={`unscheduled-sidebar-${type}-${idx}`}
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.effectAllowed = "move";
                                      e.dataTransfer.setData("application/json", JSON.stringify(item));
                                    }}
                                    style={{
                                      padding: "6px 8px",
                                      background: "#050e1a",
                                      border: "1px solid rgba(239, 68, 68, 0.2)",
                                      borderRadius: "4px",
                                      cursor: "grab",
                                      fontSize: "10px",
                                      color: "#fff",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      transition: "all 0.2s"
                                    }}
                                    title={item.title || item.name}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = "rgba(239, 68, 68, 0.15)";
                                      e.target.style.borderColor = "rgba(239, 68, 68, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = "#050e1a";
                                      e.target.style.borderColor = "rgba(239, 68, 68, 0.2)";
                                    }}
                                    onClick={() => handleViewItem && handleViewItem(item)}
                                  >
                                    {item.title || item.name}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Content Area */}
      <div style={{ flex: 1, overflowY: "auto", paddingRight: menuVisible ? "20px" : "40px", display: "flex", flexDirection: "column", transition: "padding 0.3s ease" }}>
        {/* Menu Toggle Button (visible when menu is hidden) */}
        {!menuVisible && (
          <button
            onClick={() => setMenuVisible(true)}
            style={{
              background: "transparent",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              color: "#5B9EF0",
              cursor: "pointer",
              padding: "8px 12px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "12px",
              fontWeight: "600",
              transition: "all 0.2s",
              alignSelf: "flex-start",
              marginBottom: "12px"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(59, 130, 246, 0.15)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
            }}
            title="Show menu"
          >
            ☰ Menu
          </button>
        )}
        {activeReport === "calendar" ? (
          <div>
            <h2 style={{ margin: "0 0 16px 0", color: "#5B9EF0" }}>Publication Calendar</h2>

            {/* Status Tabs */}
            <PublicationCalendar
              allItems={displayItems.filter(item =>
                (item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                 item.subtitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                 item.tag?.toLowerCase().includes(searchQuery.toLowerCase())) &&
                (!selectedCategory || item.contentType === selectedCategory)
              )}
              onEditItem={handleEditItem}
              onViewItem={handleViewItem}
              onRescheduleItem={handleRescheduleItem}
              showNextMonth={showNextMonth}
            />
          </div>
        ) : (
          <div>
            <h2 style={{ margin: "0 0 16px 0", color: "#5B9EF0" }}>Publication List</h2>

            {/* Items List */}
            <PullToRefresh onRefresh={loadAllItems}>
              {displayItems.length === 0 ? (
                <div className="empty-state">
                  {searchQuery
                    ? "No items match your search"
                    : `No ${activeSubtab === "pending" ? "pending" : activeSubtab === "published" ? "published" : activeSubtab === "unscheduled" ? "unscheduled" : "matching"} items`}
              </div>
            ) : sortedTypes.length === 0 ? (
              <div className="empty-state">No items match your search</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {Object.keys(filteredGroupedItems).flatMap((type) =>
                  filteredGroupedItems[type].map((item) => (
                        <div
                          key={`${item.contentType}-${item.id}`}
                          style={{
                            padding: "12px",
                            background: "#050e1a",
                            border: "1px solid rgba(59, 130, 246, 0.15)",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{ margin: "0 0 4px 0", color: "#fff", fontSize: "14px", fontWeight: "600", wordBreak: "break-word", display: "flex", alignItems: "center", gap: "8px" }}>
                              {item.draft && !item.scheduled ? (
                                <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }} title="Needs scheduling">
                                  {SVG_ICONS["alert-circle"]({ size: 18, color: "#EF4444" })}
                                </span>
                              ) : item.draft && item.scheduled ? (
                                <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }} title="Scheduled">
                                  {SVG_ICONS["hourglass"]({ size: 18, color: "#F59E0B" })}
                                </span>
                              ) : !item.draft ? (
                                <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }} title="Published">
                                  {SVG_ICONS["check-circle"]({ size: 18, color: "#22C55E" })}
                                </span>
                              ) : null}
                              {item.title}
                            </h4>
                            <div style={{ display: "flex", gap: "12px", alignItems: "center", fontSize: "12px", color: "#7A9CC4", flexWrap: "wrap" }}>
                              <span style={{ padding: "2px 8px", background: "rgba(91, 158, 240, 0.2)", color: "#5B9EF0", borderRadius: "4px", fontWeight: "600" }}>
                                {type}
                              </span>
                              <span style={{
                                padding: "2px 8px",
                                background: item.draft ? "rgba(245, 158, 11, 0.2)" : "rgba(34, 197, 94, 0.2)",
                                color: item.draft ? "#F59E0B" : "#22C55E",
                                borderRadius: "4px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px"
                              }}>
                                {item.draft ? (
                                  <>
                                    {SVG_ICONS["alert-circle"]({ size: 12, color: "#F59E0B" })}
                                    Draft
                                  </>
                                ) : (
                                  <>
                                    {SVG_ICONS["check-circle"]({ size: 12, color: "#22C55E" })}
                                    Published
                                  </>
                                )}
                              </span>
                              <span title={item.addedAt || item.published_at}>
                                Added: {new Date(item.addedAt || item.published_at).toLocaleDateString()}
                              </span>
                              {item.publishedDate && !item.draft && (
                                <span title={item.publishedDate} style={{ color: "#22C55E" }}>
                                  Published: {formatDateDisplay(item.publishedDate)}
                                </span>
                              )}
                              {item.scheduled && item.draft && (
                                <>
                                  {(() => {
                                    const status = getScheduleStatus(item.scheduled);
                                    return status ? (
                                      <span style={{
                                        padding: "2px 8px",
                                        background: status.bgColor,
                                        color: status.color,
                                        borderRadius: "4px",
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px"
                                      }}>
                                        {status.emoji} {status.label}
                                      </span>
                                    ) : (
                                      <span style={{ color: "#7A9CC4" }}>
                                        Scheduled: {formatDateDisplay(item.scheduled)}
                                      </span>
                                    );
                                  })()}
                                </>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "8px", flexShrink: 0, alignItems: "center" }}>
                            {item.scheduled && item.draft && (
                              <div style={{
                                fontSize: "12px",
                                color: "#7A9CC4",
                                whiteSpace: "nowrap",
                                padding: "4px 8px",
                                background: "rgba(59, 130, 246, 0.05)",
                                borderRadius: "4px"
                              }}>
                                Scheduled: {formatDateDisplay(item.scheduled)}
                              </div>
                            )}
                            <button
                              onClick={() => handleToggleDraft(item)}
                              title={item.draft ? "Publish item" : "Revert to draft"}
                              style={{
                                padding: "6px 12px",
                                background: item.draft
                                  ? "rgba(34, 197, 94, 0.1)"
                                  : "rgba(245, 158, 11, 0.1)",
                                color: item.draft ? "#22C55E" : "#F59E0B",
                                border: item.draft
                                  ? "1px solid rgba(34, 197, 94, 0.2)"
                                  : "1px solid rgba(245, 158, 11, 0.2)",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "14px",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                whiteSpace: "nowrap",
                              }}
                              onMouseEnter={(e) => {
                                if (item.draft) {
                                  e.target.style.background = "rgba(34, 197, 94, 0.2)";
                                  e.target.style.borderColor = "rgba(34, 197, 94, 0.4)";
                                } else {
                                  e.target.style.background = "rgba(245, 158, 11, 0.2)";
                                  e.target.style.borderColor = "rgba(245, 158, 11, 0.4)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (item.draft) {
                                  e.target.style.background = "rgba(34, 197, 94, 0.1)";
                                  e.target.style.borderColor = "rgba(34, 197, 94, 0.2)";
                                } else {
                                  e.target.style.background = "rgba(245, 158, 11, 0.1)";
                                  e.target.style.borderColor = "rgba(245, 158, 11, 0.2)";
                                }
                              }}
                            >
                              {item.draft ? (
                                <>
                                  {SVG_ICONS["check"]({ size: 16, color: "#22C55E" })}
                                  Publish
                                </>
                              ) : (
                                <>
                                  {SVG_ICONS["circle-x"]({ size: 16, color: "#F59E0B" })}
                                  Draft
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleViewItem(item)}
                              title="View item"
                              style={{
                                padding: "6px 12px",
                                background: "rgba(34, 197, 94, 0.1)",
                                color: "#22C55E",
                                border: "1px solid rgba(34, 197, 94, 0.2)",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "14px",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = "rgba(34, 197, 94, 0.2)";
                                e.target.style.borderColor = "rgba(34, 197, 94, 0.4)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = "rgba(34, 197, 94, 0.1)";
                                e.target.style.borderColor = "rgba(34, 197, 94, 0.2)";
                              }}
                            >
                              {SVG_ICONS["eye"]({ size: 16, color: "#22C55E" })}
                            </button>
                            <button
                              onClick={() => handleEditItem(item)}
                              title="Edit item"
                              style={{
                                padding: "6px 12px",
                                background: "rgba(59, 130, 246, 0.1)",
                                color: "#5B9EF0",
                                border: "1px solid rgba(59, 130, 246, 0.2)",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "14px",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = "rgba(59, 130, 246, 0.2)";
                                e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = "rgba(59, 130, 246, 0.1)";
                                e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                              }}
                            >
                              {SVG_ICONS["pencil"]({ size: 16, color: "#5B9EF0" })}
                            </button>
                          </div>
                        </div>
                      ))
                )}
              </div>
            )}
            </PullToRefresh>
          </div>
        )}
      </div>

      <PreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={previewTitle}
        content={previewContent}
      />

      {editingId && (
        <div className="modal-overlay" onClick={() => setEditingId(null)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ background: "#0a1628", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "12px", padding: "32px", maxWidth: "600px", width: "90%", maxHeight: "80vh", overflowY: "auto" }}>
            <h2 style={{ color: "#5B9EF0", marginBottom: "24px" }}>Edit {editFormData.contentType}</h2>

            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>Title</label>
              <input type="text" value={editFormData.title || editFormData.name || ""} onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })} style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff" }} />
            </div>

            {editFormData.contentType === "Lesson" && (
              <>
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>Subtitle</label>
                  <textarea value={editFormData.subtitle || ""} onChange={(e) => setEditFormData({ ...editFormData, subtitle: e.target.value })} rows="2" style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff" }} />
                </div>
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>Duration</label>
                  <input type="text" value={editFormData.dur || ""} onChange={(e) => setEditFormData({ ...editFormData, dur: e.target.value })} placeholder="e.g., 5 min" style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff" }} />
                </div>
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>Tag</label>
                  <input type="text" value={editFormData.tag || ""} onChange={(e) => setEditFormData({ ...editFormData, tag: e.target.value })} style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff" }} />
                </div>
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>Content (Markdown)</label>
                  <textarea value={typeof editFormData.body === "string" ? editFormData.body : ""} onChange={(e) => setEditFormData({ ...editFormData, body: e.target.value })} rows="8" placeholder="Enter content in markdown..." style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff", fontFamily: "monospace" }} />
                </div>
              </>
            )}

            {editFormData.contentType === "Prompt" && (
              <div className="form-group" style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>Text</label>
                <textarea value={editFormData.text || ""} onChange={(e) => setEditFormData({ ...editFormData, text: e.target.value })} rows="8" style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff" }} />
              </div>
            )}

            {editFormData.contentType === "Resource" && (
              <>
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>URL</label>
                  <input type="url" value={editFormData.url || ""} onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })} style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff" }} />
                </div>
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>Description</label>
                  <textarea value={editFormData.description || ""} onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })} rows="3" style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff" }} />
                </div>
              </>
            )}

            {editFormData.contentType === "News" && (
              <>
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>What does it mean to me</label>
                  <textarea value={editFormData.what_does_it_mean_to_me || ""} onChange={(e) => setEditFormData({ ...editFormData, what_does_it_mean_to_me: e.target.value })} rows="4" style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff" }} />
                </div>
                <div className="form-group" style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", color: "#9DB4D4", marginBottom: "8px", fontSize: "14px" }}>URL</label>
                  <input type="url" value={editFormData.url || ""} onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })} style={{ width: "100%", padding: "8px 12px", background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#fff" }} />
                </div>
              </>
            )}

            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", color: "#9DB4D4", fontSize: "14px", cursor: "pointer" }}>
                <input type="checkbox" checked={!editFormData.draft} onChange={(e) => setEditFormData({ ...editFormData, draft: !e.target.checked })} />
                Published
              </label>
            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button onClick={() => setEditingId(null)} style={{ padding: "8px 16px", background: "transparent", border: "1px solid rgba(59, 130, 246, 0.3)", color: "#5B9EF0", borderRadius: "6px", cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleSaveEdit} style={{ padding: "8px 16px", background: "rgba(59, 130, 246, 0.2)", border: "1px solid rgba(59, 130, 246, 0.4)", color: "#5B9EF0", borderRadius: "6px", cursor: "pointer" }}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ReportsTab.displayName = 'ReportsTab';

export default ReportsTab;
